import s from "~/pages/CameraZonesView/CameraZonesView.less";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Modal from "~/shared/ui/Fields/Modal";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  Camera,
  CameraCheckStatus,
  CameraVideo,
  useAddNotificationMutation,
  useGetCameraByIdLazyQuery,
  useGetCameraCheckListLazyQuery,
  useGetCameraVideoLazyQuery,
  useSetCameraStreamCloseMutation,
} from "~/schemas/generated/graphql";
import Button from "~/shared/ui/Fields/Button";
import clsx from "clsx";
import CameraCheckChat from "~/pages/CameraZonesView/parts/CameraCheckChat";
import CameraCheckHistory from "~/pages/CameraZonesView/parts/CameraCheckHistory";
import CameraCheckAccepting from "~/pages/CameraZonesView/parts/CameraCheckAccepting";
import { NOTIFICATIONS } from "~/constans/notifications";
import { get } from "lodash";
import StatusResolver from "~/pages/CameraZonesView/parts/StatusResolver";
import { CamerasContext } from "~/pages/CameraZonesView";
import { streamHandler } from "~/pages/CameraZonesView/utils/streamHandler";

interface ICameraStatusUpdateContext {
  reFetchAllDependencyData: () => void;
}

export const CameraStatusUpdateContext =
  createContext<ICameraStatusUpdateContext>({
    reFetchAllDependencyData: () => {},
  });

export enum CAMERA_CHAT_MODE {
  HISTORY = "HISTORY",
  ACCEPT = "ACCEPT",
  CHAT = "CHAT",
}

interface IParameters {
  openModal?: string;
  cameraId?: string;
  dialog?: string;
}

const CameraViewModal = () => {
  // Utils
  const setURLParams = useURLParamsSetter();
  const { openModal, cameraId, dialog } = useURLParamsGetter<IParameters>();
  const { fetchCameras, userCanManageCamera, userCanAuditCamera } =
    useContext(CamerasContext);

  // Ref
  const canvasRef = useRef<HTMLCanvasElement>(null);

  //Mutations
  const [addNotificationMutation] = useAddNotificationMutation();
  const [closeStream] = useSetCameraStreamCloseMutation();

  // Query
  const [getCameraInstance, getCameraInstanceState] = useGetCameraByIdLazyQuery(
    {
      fetchPolicy: "no-cache",
    }
  );
  const [getWSLink, getWSLinkState] = useGetCameraVideoLazyQuery({
    fetchPolicy: "no-cache",
    // onError(e) {
    //   addNotificationMutation({
    //     variables: {
    //       type: NOTIFICATIONS.error,
    //       content: e.message,
    //     },
    //   });
    // },
  });
  const [getCameraCheckListNotCompliant, getCameraCheckListNotCompliantState] =
    useGetCameraCheckListLazyQuery({
      fetchPolicy: "no-cache",
    });

  // State
  const [currentMode, setCurrentMode] = useState<CAMERA_CHAT_MODE>();
  const [cameraVideoStreamSettings, setCameraVideoStreamSettings] =
    useState<CameraVideo>();
  const [cameraHttpLink, setCameraHttpLink] = useState<string>();
  const [cameraInstance, setCameraInstance] = useState<Camera>();
  const [noComplaintChecksCount, setNoComplaintChecksCount] = useState(0);

  // Handlers
  const toggleHandle = (mode?: CAMERA_CHAT_MODE) => () =>
    setCurrentMode((p) => (mode ? (p === mode ? undefined : mode) : undefined));

  const handleCloseModal = () =>
    setURLParams({
      openModal: undefined,
      cameraId: undefined,
      dialog: undefined,
    });

  const fetchCameraInstance = () =>
    getCameraInstance({ variables: { id: cameraId } });

  const fetchNoComplaintChecks = () =>
    getCameraCheckListNotCompliant({
      variables: {
        cameraId,
        status: CameraCheckStatus.NotCompliant,
      },
    });

  const reFetchAllDependencyData = () => {
    fetchNoComplaintChecks();
    fetchCameraInstance();
    fetchCameras();
  };

  useEffect(() => {
    if (cameraId) getWSLink({ variables: { cameraId } });
    fetchCameraInstance();
    fetchNoComplaintChecks();
  }, [cameraId]);

  useEffect(() => {
    if (!getWSLinkState.loading && getWSLinkState.data) {
      const cameraVideoSettings = get(
        getWSLinkState.data,
        "CameraGetter.getCameraVideo"
      );
      if (cameraVideoSettings.type === "http") {
        setCameraHttpLink(cameraVideoSettings.url);
      } else {
        setCameraVideoStreamSettings(cameraVideoSettings);
      }
    }
  }, [getWSLinkState.data]);

  useEffect(() => {
    if (!!cameraVideoStreamSettings?.url) {
      const player = streamHandler(
        cameraVideoStreamSettings.url,
        canvasRef.current
      );
      return () => {
        const { pid } = cameraVideoStreamSettings;
        closeStream({ variables: { pid } }).then(() => {
          !!player?.source?.socket?.close && player?.source?.socket?.close();
        });
      };
    }
  }, [cameraVideoStreamSettings]);

  useEffect(() => {
    if (dialog) setCurrentMode(CAMERA_CHAT_MODE.CHAT);
  }, [dialog]);

  useEffect(() => {
    if (currentMode !== CAMERA_CHAT_MODE.CHAT)
      setURLParams({ dialog: undefined });
  }, [currentMode]);

  useEffect(() => {
    if (getCameraInstanceState && !getCameraInstanceState.loading) {
      setCameraInstance(
        get(getCameraInstanceState.data, "CameraGetter.getCamera")
      );
    }
  }, [getCameraInstanceState.data]);

  useEffect(() => {
    setNoComplaintChecksCount(
      get(
        getCameraCheckListNotCompliantState.data,
        "CameraGetter.getCameraChecks.total",
        0
      )
    );
  }, [getCameraCheckListNotCompliantState.data]);

  return (
    <CameraStatusUpdateContext.Provider value={{ reFetchAllDependencyData }}>
      <Modal
        isOpen={openModal === "ViewCamera"}
        title={"Просмотр"}
        actionClose={handleCloseModal}
        customClassName={s.modal}
      >
        <div className={s.container}>
          <div className={s.subContainer}>
            <div className={s.cameraWrapper}>
              <div className={s.statusWrapper}>
                {cameraInstance && (
                  <StatusResolver status={cameraInstance.status} />
                )}
              </div>
              {cameraHttpLink && (
                <div className={s.noticeWrapper}>
                  <span>
                    Камера привязана как внешний HTTP ресурс, для просмотра
                    пройдите по
                    <a href={cameraHttpLink} target="_blank">
                      {" "}
                      ссылке
                    </a>
                  </span>
                </div>
              )}
              {cameraVideoStreamSettings && (
                <canvas ref={canvasRef} style={{ width: "100%" }} />
              )}
            </div>

            {!!currentMode && (
              <div className={s.commentWrapper}>
                {(() => {
                  switch (currentMode) {
                    case CAMERA_CHAT_MODE.ACCEPT:
                      return (
                        userCanManageCamera && (
                          <CameraCheckAccepting
                            currentStatus={cameraInstance.status}
                            onlyNotCompliant={noComplaintChecksCount}
                          />
                        )
                      );
                    case CAMERA_CHAT_MODE.CHAT:
                      return (
                        userCanAuditCamera && (
                          <CameraCheckChat
                            toggleModeHandle={toggleHandle(
                              CAMERA_CHAT_MODE.HISTORY
                            )}
                          />
                        )
                      );
                    case userCanAuditCamera && CAMERA_CHAT_MODE.HISTORY:
                      return <CameraCheckHistory />;
                  }
                })()}
              </div>
            )}
          </div>
          <div className={s.check}>
            {userCanManageCamera && (
              <Button
                onClick={toggleHandle(CAMERA_CHAT_MODE.ACCEPT)}
                txtBtn={"Согласование"}
                customClassName={clsx(s.btn, {
                  [s.selected]: currentMode === CAMERA_CHAT_MODE.ACCEPT,
                })}
              />
            )}
            {(userCanAuditCamera || userCanManageCamera) && (
              <Button
                onClick={toggleHandle(CAMERA_CHAT_MODE.HISTORY)}
                txtBtn={"Просмотр истории"}
                customClassName={clsx(s.btn, {
                  [s.selected]: currentMode === CAMERA_CHAT_MODE.HISTORY,
                })}
              />
            )}
          </div>
        </div>
      </Modal>
    </CameraStatusUpdateContext.Provider>
  );
};

export default CameraViewModal;
